import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ResumeWorkflowDialogProps {
  open: boolean;
  status: string;
  startedAt: string | null;
  onResume: () => void;
  onStartFresh: () => void;
}

/** Human-readable label for the workflow status shown in the dialog. */
function formatStatus(status: string): string {
  switch (status) {
    case 'awaiting_review':
      return 'awaiting your review';
    case 'scores_ready':
      return 'scores ready for review';
    case 'generating_changes':
      return 'generating prompt changes';
    case 'running_experiments':
    case 'running_preview_experiments':
    case 'experiments_in_progress':
      return 'running simulations';
    case 'polling_scores':
    case 'polling_preview_scores':
    case 'scoring_preview':
      return 'scoring results';
    case 'optimizing':
      return 'optimizing prompt';
    case 'applying_changes':
      return 'applying changes';
    case 'ab_testing':
      return 'running A/B tests';
    default:
      return 'in progress';
  }
}

function formatTimeAgo(iso: string | null): string | null {
  if (!iso) return null;
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ago`;
}

export default function ResumeWorkflowDialog({
  open,
  status,
  startedAt,
  onResume,
  onStartFresh,
}: ResumeWorkflowDialogProps) {
  const timeAgo = formatTimeAgo(startedAt);

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Resume previous workflow?</AlertDialogTitle>
          <AlertDialogDescription>
            You have a workflow that is {formatStatus(status)}
            {timeAgo ? ` (started ${timeAgo})` : ''}.
            Would you like to pick up where you left off or start fresh?
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onStartFresh}>
            Start Fresh
          </AlertDialogCancel>
          <AlertDialogAction onClick={onResume}>
            Resume Workflow
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
